import React from 'react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { PaywallDialogViewModel } from '../paywall/PaywallDialogViewModel';
import { AircraftResponse } from './FleetManagmentClient';
import { AIRCRAFT_DEFAULT_LABELS } from './labels';
import { HeartIcon } from '../components/icons/Icons';
import { CardFooter } from '../components/card/CardFooter';
import { TrackingService } from '../support/tracking/TrackingService';

export interface IDefaultAircraftFooterProps {
    aircraft: AircraftResponse;
    readonly?: boolean;
    onChange: (aircraft: AircraftResponse) => void;
    trackingFrom: 'equipment' | 'equipment detail';
}

export const DefaultAircraftFooter: React.FC<IDefaultAircraftFooterProps> = ({ aircraft, readonly, trackingFrom, onChange }) => {
    const tracker = useInstance(TrackingService);

    const paywall = useInstance(PaywallDialogViewModel);
    useSubscription(() => paywall);

    if (aircraft.status !== 'active') {
        return null;
    }

    const onClickFooter = async () => {
        const upgraded = await paywall.requirePurchase({ triggerSource: 'equipment default' });
        if (upgraded) {
            const defaultType = (!aircraft.defaultType ? 'primary' : aircraft.defaultType === 'primary' ? 'backup' : undefined);
            onChange({ ...aircraft, defaultType });

            if (!defaultType) {
                tracker.track('Equipment Default Removed', () => ({
                    equipmentType: 'Drone',
                    from: trackingFrom
                }));
            } else {
                tracker.track('Equipment Defaulted', () => ({
                    equipmentType: 'Drone',
                    defaultSetting: defaultType,
                    from: trackingFrom
                }));
            }
        }
    };

    if (readonly && !aircraft.defaultType) {
        return null;
    }

    return (
        <CardFooter
            size="large"
            message={aircraft.defaultType ? AIRCRAFT_DEFAULT_LABELS[aircraft.defaultType] : 'Make Default'}
            icon={aircraft.defaultType ? HeartIcon.raw : HeartIcon.rawOutline}
            color={aircraft.defaultType === 'backup' ? 'medium' : 'tertiary'}
            clear={!aircraft.defaultType}
            onClick={!readonly ? onClickFooter : undefined}
        />
    );
};
